import {
  STUDIO_SPATIAL_UNITS,
  createStudioSpatialDocument,
  validateStudioSpatialDocument,
} from "../StudioSpatialEngine";

export const STUDIO_DOCUMENT_SCHEMA_VERSION = 1;

const DEFAULT_CANVAS = Object.freeze({
  width: 900,
  height: 640,
  gridSize: 32,
  zoomDefault: 1,
});

export class StudioDocumentError extends Error {
  constructor(message, { code = "STUDIO_DOCUMENT_ERROR", issues = [] } = {}) {
    super(message);
    this.name = "StudioDocumentError";
    this.code = code;
    this.issues = Array.isArray(issues) ? [...issues] : [];
  }
}

export function createStudioDocument(
  overrides = {},
  { createId = createDefaultDocumentId } = {}
) {
  if (typeof createId !== "function") {
    throw new StudioDocumentError(
      "StudioDocumentEngine requires a createId callback.",
      {
        code: "INVALID_ID_FACTORY",
      }
    );
  }

  return normalizeStudioDocument({
    schemaVersion:
      overrides.schemaVersion ??
      STUDIO_DOCUMENT_SCHEMA_VERSION,
    uuid:
      overrides.uuid ||
      createId({
        prefix: "studio-document",
        studioType: overrides.studioType,
      }),
    studioType: overrides.studioType || "generic",
    name: overrides.name || "Studio document",
    canvas: overrides.canvas,
    spatial: overrides.spatial,
    scenes: overrides.scenes,
    metadata: overrides.metadata,
    extensions: overrides.extensions,
  });
}

export function normalizeStudioDocument(document = {}) {
  if (!isPlainObject(document)) {
    throw new StudioDocumentError(
      "Studio document must be a plain object.",
      {
        code: "INVALID_DOCUMENT",
      }
    );
  }

  const canvas = isPlainObject(document.canvas)
    ? document.canvas
    : {};

  const legacyCanvas = {
    width: normalizePositiveNumber(
      canvas.width,
      DEFAULT_CANVAS.width
    ),
    height: normalizePositiveNumber(
      canvas.height,
      DEFAULT_CANVAS.height
    ),
    gridSize: normalizePositiveNumber(
      canvas.gridSize,
      DEFAULT_CANVAS.gridSize
    ),
    zoomDefault: normalizePositiveNumber(
      canvas.zoomDefault,
      DEFAULT_CANVAS.zoomDefault
    ),
  };

  const spatialSource = isPlainObject(document.spatial)
    ? document.spatial
    : {
        width: legacyCanvas.width,
        height: legacyCanvas.height,
        unit: STUDIO_SPATIAL_UNITS.PIXEL,
        grid: {
          size: legacyCanvas.gridSize,
        },
      };

  const normalizedSpatial =
    createStudioSpatialDocument(spatialSource);

  // Spatial is the persistent geometry authority.
  // Canvas remains a deterministic legacy projection until
  // the schema migration removes the historical contract.
  const normalizedCanvas = {
    width: normalizedSpatial.width,
    height: normalizedSpatial.height,
    gridSize: normalizedSpatial.grid.size,
    zoomDefault: legacyCanvas.zoomDefault,
  };

  const normalized = {
    schemaVersion: normalizePositiveInteger(
      document.schemaVersion,
      STUDIO_DOCUMENT_SCHEMA_VERSION
    ),
    uuid: normalizeString(document.uuid),
    studioType: normalizeString(
      document.studioType,
      "generic"
    ),
    name: normalizeString(
      document.name,
      "Studio document"
    ),
    canvas: normalizedCanvas,
    spatial: normalizedSpatial,
    scenes: Array.isArray(document.scenes)
      ? cloneJsonValue(document.scenes, "scenes")
      : [],
    metadata: isPlainObject(document.metadata)
      ? cloneJsonValue(document.metadata, "metadata")
      : {},
    extensions: isPlainObject(document.extensions)
      ? cloneJsonValue(document.extensions, "extensions")
      : {},
  };

  return normalized;
}

export function validateStudioDocument(document) {
  const issues = [];

  if (!isPlainObject(document)) {
    return {
      valid: false,
      issues: [
        createIssue(
          "$",
          "INVALID_DOCUMENT",
          "Studio document must be a plain object."
        ),
      ],
    };
  }

  if (
    !Number.isInteger(document.schemaVersion) ||
    document.schemaVersion < 1
  ) {
    issues.push(
      createIssue(
        "schemaVersion",
        "INVALID_SCHEMA_VERSION",
        "schemaVersion must be a positive integer."
      )
    );
  }

  if (!isNonEmptyString(document.uuid)) {
    issues.push(
      createIssue(
        "uuid",
        "INVALID_UUID",
        "uuid must be a non-empty string."
      )
    );
  }

  if (!isNonEmptyString(document.studioType)) {
    issues.push(
      createIssue(
        "studioType",
        "INVALID_STUDIO_TYPE",
        "studioType must be a non-empty string."
      )
    );
  }

  if (!isNonEmptyString(document.name)) {
    issues.push(
      createIssue(
        "name",
        "INVALID_NAME",
        "name must be a non-empty string."
      )
    );
  }

  validateCanvas(document.canvas, issues);

  if (document.spatial !== undefined) {
    validateSpatial(document.spatial, issues);
  }

  if (!Array.isArray(document.scenes)) {
    issues.push(
      createIssue(
        "scenes",
        "INVALID_SCENES",
        "scenes must be an array."
      )
    );
  }

  if (!isPlainObject(document.metadata)) {
    issues.push(
      createIssue(
        "metadata",
        "INVALID_METADATA",
        "metadata must be a plain object."
      )
    );
  }

  if (!isPlainObject(document.extensions)) {
    issues.push(
      createIssue(
        "extensions",
        "INVALID_EXTENSIONS",
        "extensions must be a plain object."
      )
    );
  }

  if (issues.length === 0) {
    try {
      cloneJsonValue(document, "$");
    } catch (error) {
      issues.push(
        createIssue(
          "$",
          "NON_SERIALIZABLE_DOCUMENT",
          error.message
        )
      );
    }
  }

  return {
    valid: issues.length === 0,
    issues,
  };
}

export function assertValidStudioDocument(document) {
  const validation = validateStudioDocument(document);

  if (!validation.valid) {
    throw new StudioDocumentError(
      "Studio document validation failed.",
      {
        code: "STUDIO_DOCUMENT_VALIDATION_FAILED",
        issues: validation.issues,
      }
    );
  }

  return document;
}

export function cloneStudioDocument(document) {
  assertValidStudioDocument(document);
  return cloneJsonValue(document, "$");
}

function validateSpatial(spatial, issues) {
  const validation =
    validateStudioSpatialDocument(spatial);

  for (const message of validation.errors) {
    issues.push(
      createIssue(
        "spatial",
        "INVALID_SPATIAL_DOCUMENT",
        message
      )
    );
  }
}

function validateCanvas(canvas, issues) {
  if (!isPlainObject(canvas)) {
    issues.push(
      createIssue(
        "canvas",
        "INVALID_CANVAS",
        "canvas must be a plain object."
      )
    );
    return;
  }

  for (const field of [
    "width",
    "height",
    "gridSize",
    "zoomDefault",
  ]) {
    if (
      typeof canvas[field] !== "number" ||
      !Number.isFinite(canvas[field]) ||
      canvas[field] <= 0
    ) {
      issues.push(
        createIssue(
          `canvas.${field}`,
          "INVALID_CANVAS_VALUE",
          `${field} must be a positive finite number.`
        )
      );
    }
  }
}

function cloneJsonValue(value, path, seen = new WeakSet()) {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new StudioDocumentError(
        `${path} contains a non-finite number.`,
        {
          code: "NON_SERIALIZABLE_VALUE",
        }
      );
    }

    return value;
  }

  if (
    typeof value === "undefined" ||
    typeof value === "function" ||
    typeof value === "symbol" ||
    typeof value === "bigint"
  ) {
    throw new StudioDocumentError(
      `${path} contains a non-serializable value.`,
      {
        code: "NON_SERIALIZABLE_VALUE",
      }
    );
  }

  if (typeof value !== "object") {
    throw new StudioDocumentError(
      `${path} contains an unsupported value.`,
      {
        code: "NON_SERIALIZABLE_VALUE",
      }
    );
  }

  if (seen.has(value)) {
    throw new StudioDocumentError(
      `${path} contains a circular reference.`,
      {
        code: "CIRCULAR_REFERENCE",
      }
    );
  }

  seen.add(value);

  if (Array.isArray(value)) {
    const result = value.map((item, index) =>
      cloneJsonValue(item, `${path}[${index}]`, seen)
    );
    seen.delete(value);
    return result;
  }

  if (!isPlainObject(value)) {
    seen.delete(value);
    throw new StudioDocumentError(
      `${path} contains a non-plain object.`,
      {
        code: "NON_SERIALIZABLE_VALUE",
      }
    );
  }

  const result = {};

  for (const [key, item] of Object.entries(value)) {
    result[key] = cloneJsonValue(
      item,
      `${path}.${key}`,
      seen
    );
  }

  seen.delete(value);
  return result;
}

function normalizeString(value, fallback = "") {
  return typeof value === "string" && value.trim()
    ? value.trim()
    : fallback;
}

function normalizePositiveNumber(value, fallback) {
  const numeric = Number(value);

  return Number.isFinite(numeric) && numeric > 0
    ? numeric
    : fallback;
}

function normalizePositiveInteger(value, fallback) {
  const numeric = Number(value);

  return Number.isInteger(numeric) && numeric > 0
    ? numeric
    : fallback;
}

function createIssue(path, code, message) {
  return {
    path,
    code,
    message,
  };
}

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function isPlainObject(value) {
  if (
    value === null ||
    typeof value !== "object" ||
    Array.isArray(value)
  ) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);

  return prototype === Object.prototype || prototype === null;
}

function createDefaultDocumentId() {
  return `studio-document-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

export {
  STUDIO_OBJECT_DEFAULTS,
  STUDIO_OBJECT_DEFAULT_SPATIAL,
} from "./StudioObjectContract";

export {
  createStudioObject,
} from "./createStudioObject";

export {
  validateStudioObject,
} from "./validateStudioObject";

export {
  toStudioInspectorObject,
} from "./adapters/studioInspectorAdapter";
